import { FC, useState } from 'react';
import { Lightbulb, Sparkles, ChevronRight, Check } from 'lucide-react';

import { useTranslation } from '@app/app/i18n';
import { Popover, PopoverContent, PopoverTrigger } from '@app/shared/ui/popover';
import { useGetCommonInitQuery } from '@app/store/api/user.api';
import { isBoxedMode } from '@app/utils/user-status.utils';

import { useOnboardingState } from '../hooks/use-onboarding-state';
import { TOUR_REGISTRY } from '../model/onboarding.constants';
import type { TourConfig } from '../model/onboarding.types';

import { cvaFabButton, cvaPopoverContent, cvaTourCard } from './onboarding-fab-styles';

const iconMap: Record<string, FC<{ className?: string }>> = {
  Sparkles,
  Lightbulb,
};

export const OnboardingFab: FC = () => {
  const { t } = useTranslation('finalize-stage');
  const [isOpen, setIsOpen] = useState(false);

  const { data: commonInit } = useGetCommonInitQuery(null);
  const isBoxed = isBoxedMode(commonInit?.result ?? null);

  const { startTour, isTourDismissed } = useOnboardingState();

  // В box-версии onboarding полностью отключен
  if (isBoxed) return null;

  const isTourAvailable = (tour: TourConfig): boolean => {
    if (tour.conditions?.excludeBoxed && isBoxed) return false;
    // requireImages no longer blocks the button -
    // tour adapts to "add image" path when no images on canvas
    return true;
  };

  const handleTourClick = (tourId: string) => {
    setIsOpen(false);
    // Small delay to let popover close before starting tour
    setTimeout(() => {
      startTour(tourId);
    }, 200);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={cvaFabButton({ isOpen })}
          aria-label={t('onboarding.fab.title')}
          data-testid="onboarding-fab"
          data-onboarding-id="onboarding-fab"
        >
          <Lightbulb className="w-5 h-5" />
        </button>
      </PopoverTrigger>

      <PopoverContent side="top" align="end" sideOffset={8} className={cvaPopoverContent()}>
        <h3 className="text-sm font-semibold text-general_text_MII_default mb-2 px-1">
          {t('onboarding.fab.title')}
        </h3>

        <div className="flex flex-col gap-1">
          {TOUR_REGISTRY.map((tour) => {
            const available = isTourAvailable(tour);
            const dismissed = isTourDismissed(tour.id);
            const IconComponent = iconMap[tour.icon] || Lightbulb;

            return (
              <button
                key={tour.id}
                className={cvaTourCard({ disabled: !available })}
                onClick={() => available && handleTourClick(tour.id)}
                disabled={!available}
              >
                <div className="shrink-0 w-8 h-8 rounded-md bg-general_background_MIII_500_dark flex items-center justify-center">
                  <IconComponent className="w-4 h-4 text-general_MI_500_default" />
                </div>

                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-medium text-general_text_MII_default truncate">
                    {t(tour.titleKey)}
                  </p>
                  <p className="text-[10px] text-general_text_MII_secondary_i truncate">
                    {t(tour.descriptionKey)}
                  </p>
                </div>

                <div className="shrink-0">
                  {dismissed ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-general_text_MII_secondary_i" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
